import {
  MAX_JALAALI_YEAR,
  MIN_JALAALI_YEAR,
  isValidJalaaliDate,
  jalaaliMonthLength,
  toGregorian,
  toJalaali,
} from 'jalaali-js';
import { normalizeDigits } from './persianUi.js';

const PLACEHOLDER = '—';
const TEHRAN_TIME_ZONE = 'Asia/Tehran';
const PERSIAN_DIGITS = '۰۱۲۳۴۵۶۷۸۹';
const gregorianPartsFormatter = new Intl.DateTimeFormat('en-u-ca-gregory-nu-latn', {
  timeZone: TEHRAN_TIME_ZONE,
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
});

const dateFormatters = new Map();
const getDateFormatter = (style, includeTime) => {
  const key = `${style}:${includeTime}`;
  if (!dateFormatters.has(key)) {
    dateFormatters.set(key, new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
      timeZone: TEHRAN_TIME_ZONE,
      numberingSystem: 'arabext',
      year: 'numeric',
      month: style === 'long' ? 'long' : '2-digit',
      day: '2-digit',
      ...(includeTime ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } : {}),
    }));
  }
  return dateFormatters.get(key);
};

const dateOnlyPattern = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDateValue = (value) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : { date: value, dateOnly: false };
  }
  if (typeof value !== 'string' || !value.trim()) return null;

  const dateOnlyMatch = value.trim().match(dateOnlyPattern);
  if (dateOnlyMatch) {
    const [, yearText, monthText, dayText] = dateOnlyMatch;
    const year = Number(yearText);
    const month = Number(monthText);
    const day = Number(dayText);
    const date = new Date(0);
    date.setUTCFullYear(year, month - 1, day);
    date.setUTCHours(12, 0, 0, 0);
    if (
      month < 1 || month > 12 || day < 1
      || date.getUTCFullYear() !== year
      || date.getUTCMonth() !== month - 1
      || date.getUTCDate() !== day
    ) return null;
    return { date, dateOnly: true };
  }

  const text = value.trim();
  const hasTimeZone = /(?:Z|[+-]\d{2}:\d{2})$/i.test(text);
  const date = new Date(hasTimeZone ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? null : { date, dateOnly: false };
};

const getGregorianParts = (date) => {
  const parts = Object.fromEntries(
    gregorianPartsFormatter.formatToParts(date).map(({ type, value }) => [type, value]),
  );
  return {
    gy: Number(parts.year),
    gm: Number(parts.month),
    gd: Number(parts.day),
  };
};

const isSupportedJalaliYear = (year) => (
  Number.isInteger(year) && year >= MIN_JALAALI_YEAR && year <= MAX_JALAALI_YEAR
);

const pad2 = (value) => String(value).padStart(2, '0');

export const fmt = (n) => {
  const num = Number(n);
  return Number.isNaN(num) ? '—' : num.toLocaleString('fa-IR');
};

export const toPersianDigits = (value) =>
  String(value ?? '').replace(/\d/g, (digit) => PERSIAN_DIGITS[Number(digit)]);

export const formatOrderNumber = (value) => (
  toPersianDigits(value) || PLACEHOLDER
);

export const formatToman = (value) => `${fmt(value)} تومان`;

export const toJalaliParts = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  const { gy, gm, gd } = getGregorianParts(parsed.date);
  if (gy < MIN_JALAALI_YEAR + 621 || gy > MAX_JALAALI_YEAR + 622) return null;
  return toJalaali(gy, gm, gd);
};

export const isoToJalali = (value) => toJalaliParts(value);

export const fromJalaliParts = (jy, jm, jd) => {
  if (
    !isSupportedJalaliYear(jy)
    || !Number.isInteger(jm) || jm < 1 || jm > 12
    || !Number.isInteger(jd) || jd < 1
    || !isValidJalaaliDate(jy, jm, jd)
  ) return null;
  return toGregorian(jy, jm, jd);
};

export const jalaliToISO = (jy, jm, jd) => {
  const gregorian = fromJalaliParts(jy, jm, jd);
  if (!gregorian) return null;
  const { gy, gm, gd } = gregorian;
  return `${String(gy).padStart(4, '0')}-${pad2(gm)}-${pad2(gd)}`;
};

export const addISODateDays = (value, days) => {
  const parsed = parseDateValue(value);
  if (!parsed || !Number.isInteger(days)) return null;
  const { gy, gm, gd } = getGregorianParts(parsed.date);
  const date = new Date(0);
  date.setUTCFullYear(gy, gm - 1, gd);
  date.setUTCHours(12, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() + days);
  const next = getGregorianParts(date);
  return `${String(next.gy).padStart(4, '0')}-${pad2(next.gm)}-${pad2(next.gd)}`;
};

export const addJalaliMonths = (value, amount) => {
  const parts = toJalaliParts(value);
  if (!parts || !Number.isInteger(amount)) return null;
  const monthIndex = (parts.jy * 12) + parts.jm - 1 + amount;
  const jy = Math.floor(monthIndex / 12);
  const jm = ((monthIndex % 12) + 12) % 12 + 1;
  if (!isSupportedJalaliYear(jy)) return null;
  return jalaliToISO(jy, jm, Math.min(parts.jd, jalaaliMonthLength(jy, jm)));
};

export const jalaliWeekdayIndex = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return null;
  const { gy, gm, gd } = getGregorianParts(parsed.date);
  const weekday = new Date(Date.UTC(gy, gm - 1, gd)).getUTCDay();
  return (weekday + 1) % 7;
};

export const parseJalaliDate = (value) => {
  const match = normalizeDigits(value)
    .trim()
    .match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/);
  if (!match) return null;
  return jalaliToISO(Number(match[1]), Number(match[2]), Number(match[3]));
};

export const formatJalaliDate = (value, { includeTime = false, style = 'short' } = {}) => {
  const parsed = parseDateValue(value);
  if (!parsed) return PLACEHOLDER;
  const showTime = includeTime && !parsed.dateOnly;
  return getDateFormatter(style, showTime).format(parsed.date);
};

export const formatJalaliDateTime = (value) =>
  formatJalaliDate(value, { includeTime: true, style: 'long' });

export const formatJalaliMonth = (value) => {
  const parsed = parseDateValue(value);
  if (!parsed) return PLACEHOLDER;
  return new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    timeZone: TEHRAN_TIME_ZONE,
    numberingSystem: 'arabext',
    year: 'numeric',
    month: 'long',
  }).format(parsed.date);
};

export const jalaaliMonthDays = (jy, jm) => (
  isSupportedJalaliYear(jy) && Number.isInteger(jm) && jm >= 1 && jm <= 12
    ? jalaaliMonthLength(jy, jm)
    : 0
);